import type { Listing, ListingStepType } from "@repo/database";

type PartialListingWithStep = Partial<Listing> & { step: ListingStepType };

export interface ListingEditorStepData {
  identifier: ListingStepType;
  getPreviousStep(listing: Partial<Listing>): ListingStepType | null;
  getNextStep(
    listing: Partial<Listing>,
    metadata?: any,
  ): ListingStepType | null;
  getStepPathname(): string;
  hierarchy: number;
  openConditions?: (listing: Partial<Listing>) => boolean;
}

function isAssetOrHybridSale(listing: Partial<Listing>): boolean {
  return listing.saleType === "ASSET_SALE" || listing.saleType === "HYBRID";
}

export function isBrokerManagedListing(listing: Partial<Listing>): boolean {
  return !listing.ownerId && Boolean(listing.sellerAgentId);
}

function listingCountryCode(listing: Partial<Listing>): string | null {
  const code = (
    listing as {
      business?: {
        address?: { country?: { code?: string | null } | null } | null;
      } | null;
    }
  ).business?.address?.country?.code;
  const normalized = code?.trim().toUpperCase();
  return normalized || null;
}

/** SBA loans are a US program. They do not apply in Canada or New Zealand. */
export function sbaPrequalificationAvailableInCountry(
  countryCode: string | null | undefined,
): boolean {
  const country = countryCode?.trim().toUpperCase();
  return country !== "CA" && country !== "NZ";
}

/** SBA loans are a US program. Canada and New Zealand listings skip that step. */
export function sbaPrequalificationApplies(listing: Partial<Listing>): boolean {
  if (!sbaPrequalificationAvailableInCountry(listingCountryCode(listing))) {
    return false;
  }
  return listing.saleType !== "ASSET_SALE" && !!listing.financingAvailable;
}

export const listingEditorSteps: ListingEditorStepData[] = [
  {
    identifier: "LOCATION",
    getPreviousStep: () => null,
    getNextStep: () => "BUSINESS_KIND",
    getStepPathname: () => "business/location",
    hierarchy: 0,
  },
  {
    identifier: "CONFIRM_LOCATION",
    getPreviousStep: () => "LOCATION",
    getNextStep: () => "BUSINESS_KIND",
    getStepPathname: () => "business/confirm-location",
    hierarchy: 1,
  },
  {
    identifier: "BUSINESS_KIND",
    getPreviousStep: () => "LOCATION",
    getNextStep: () => "SALE_TYPE",
    getStepPathname: () => "business/kind",
    hierarchy: 2,
  },
  {
    identifier: "SALE_TYPE",
    getPreviousStep: () => "BUSINESS_KIND",
    getNextStep: () => "BASIC_DETAILS",
    getStepPathname: () => "business/sale-type",
    hierarchy: 3,
  },
  {
    identifier: "BASIC_DETAILS",
    getPreviousStep: () => "SALE_TYPE",
    getNextStep: (listing) =>
      isBrokerManagedListing(listing)
        ? "FINANCIALS"
        : listing.saleType === "ASSET_SALE"
          ? "DOCUMENTS"
          : "PROPERTY_START",
    getStepPathname: () => "business/basic-details",
    hierarchy: 4,
  },
  {
    identifier: "PROPERTY_START",
    getPreviousStep: () => "BASIC_DETAILS",
    getNextStep: (listing) => {
      if (!listing.propertyKind) {
        return null;
      }

      switch (listing.propertyKind) {
        case "NONE":
          return null;
        case "OWNED":
          return "PROPERTY_OWNED";
        case "RENTED":
          return "PROPERTY_RENTED";
      }
    },
    getStepPathname: () => "business/property-start",
    hierarchy: 5,
    openConditions: (listing) => listing.saleType !== "ASSET_SALE",
  },
  {
    identifier: "PROPERTY_RENTED",
    getPreviousStep: () => "PROPERTY_START",
    getNextStep: (listing) =>
      isBrokerManagedListing(listing) ? "FINANCIALS" : "DOCUMENTS",
    getStepPathname: () => "business/property-rent",
    hierarchy: 6,
    openConditions: (listing) =>
      listing.saleType !== "ASSET_SALE" && listing.propertyKind === "RENTED",
  },
  {
    identifier: "PROPERTY_OWNED",
    getPreviousStep: () => "PROPERTY_START",
    getNextStep: (listing) =>
      isBrokerManagedListing(listing) ? "FINANCIALS" : "DOCUMENTS",
    getStepPathname: () => "business/property-owned",
    hierarchy: 6,
    openConditions: (listing) =>
      listing.saleType !== "ASSET_SALE" && listing.propertyKind === "OWNED",
  },
  {
    identifier: "DOCUMENTS",
    getPreviousStep: (listing) => {
      if (listing.saleType === "ASSET_SALE") {
        return "BASIC_DETAILS";
      }
      if (!listing.propertyKind) {
        return "PROPERTY_START";
      }
      switch (listing.propertyKind) {
        case "NONE":
          return "PROPERTY_START";
        case "OWNED":
          return "PROPERTY_OWNED";
        case "RENTED":
          return "PROPERTY_RENTED";
      }
    },
    getNextStep: () => "FINANCIALS",
    getStepPathname: () => "business/documents",
    hierarchy: 7,
    openConditions: (listing) => !isBrokerManagedListing(listing),
  },
  {
    identifier: "FINANCIALS",
    getPreviousStep: (listing) =>
      isBrokerManagedListing(listing) ? "BASIC_DETAILS" : "DOCUMENTS",
    getNextStep: (listing) => {
      if (isAssetOrHybridSale(listing)) {
        return "ASKING_PRICE";
      }
      return "VALUATION";
    },
    getStepPathname: () => "business/financials",
    hierarchy: 8,
  },
  {
    identifier: "VALUATION",
    getPreviousStep: () => "FINANCIALS",
    getNextStep: (
      _listing,
      metadata?: {
        wantsValuation?: boolean;
      },
    ) => {
      return metadata?.wantsValuation ? "VALUATION_DETAILS" : "ASKING_PRICE";
    },
    getStepPathname: () => "business/valuation",
    hierarchy: 9,
    openConditions: (listing) => !isAssetOrHybridSale(listing),
  },
  {
    identifier: "VALUATION_DETAILS",
    getPreviousStep: () => "VALUATION",
    getNextStep: (listing) => {
      return listing.minValuation && listing.maxValuation
        ? "VALUATION_RESULTS"
        : "ASKING_PRICE";
    },
    getStepPathname: () => "business/valuation-details",
    hierarchy: 10,
    openConditions: (listing) =>
      !isAssetOrHybridSale(listing),
  },
  {
    identifier: "VALUATION_RESULTS",
    getPreviousStep: () => "VALUATION_DETAILS",
    getNextStep: () => "ASKING_PRICE",
    getStepPathname: () => "business/valuation-results",
    hierarchy: 11,
    openConditions: (listing) =>
      !isAssetOrHybridSale(listing) &&
      !!listing.minValuation &&
      !!listing.maxValuation,
  },
  {
    identifier: "ASKING_PRICE",
    getPreviousStep: (listing) => {
      if (isAssetOrHybridSale(listing)) {
        return "FINANCIALS";
      }
      return listing.minValuation && listing.maxValuation
        ? "VALUATION_RESULTS"
        : "VALUATION";
    },
    getNextStep: (listing) =>
      listing.saleType === "ASSET_SALE" ? "PHOTOS" : "FINANCING",
    getStepPathname: () => "business/asking-price",
    hierarchy: 12,
  },
  {
    identifier: "FINANCING",
    getPreviousStep: () => "ASKING_PRICE",
    getNextStep: (listing) => {
      if (!listing.financingAvailable) return "TRAINING";
      return sbaPrequalificationApplies(listing)
        ? "SBA_PREQUALIFICATION"
        : "MINIMUM_DOWN_PAYMENT";
    },
    getStepPathname: () => "business/financing",
    hierarchy: 13,
    openConditions: (listing) => listing.saleType !== "ASSET_SALE",
  },
  {
    identifier: "SBA_PREQUALIFICATION",
    getPreviousStep: () => "FINANCING",
    getNextStep: () => "MINIMUM_DOWN_PAYMENT",
    getStepPathname: () => "business/sba-prequalification",
    hierarchy: 14,
    openConditions: (listing) => sbaPrequalificationApplies(listing),
  },
  {
    identifier: "MINIMUM_DOWN_PAYMENT",
    getPreviousStep: (listing) =>
      sbaPrequalificationApplies(listing)
        ? "SBA_PREQUALIFICATION"
        : "FINANCING",
    getNextStep: () => "TRAINING",
    getStepPathname: () => "business/minimum-down-payment",
    hierarchy: 15,
    openConditions: (listing) =>
      listing.saleType !== "ASSET_SALE" && !!listing.financingAvailable,
  },
  {
    identifier: "TRAINING",
    getPreviousStep: (listing) => {
      return listing.financingAvailable ? "MINIMUM_DOWN_PAYMENT" : "FINANCING";
    },
    getNextStep: (listing) => {
      return listing.trainingAvailable ? "TRAINING_DETAILS" : "PHOTOS";
    },
    getStepPathname: () => "business/training",
    hierarchy: 16,
    openConditions: (listing) => listing.saleType !== "ASSET_SALE",
  },
  {
    identifier: "TRAINING_DETAILS",
    getPreviousStep: () => "TRAINING",
    getNextStep: () => "PHOTOS",
    getStepPathname: () => "business/training-details",
    hierarchy: 17,
    openConditions: (listing) =>
      listing.saleType !== "ASSET_SALE" && !!listing.trainingAvailable,
  },
  {
    identifier: "PHOTOS",
    getPreviousStep: (listing) => {
      if (listing.saleType === "ASSET_SALE") {
        return "ASKING_PRICE";
      }
      return listing.trainingAvailable ? "TRAINING_DETAILS" : "TRAINING";
    },
    getNextStep: () => "PITCH",
    getStepPathname: () => "media/photos",
    hierarchy: 18,
  },
  {
    identifier: "PITCH",
    getPreviousStep: () => "PHOTOS",
    getNextStep: (listing) => {
      if (
        listing.sellerAgentId ||
        listing.usesRecommendedAgent ||
        listing.wantsAgentMatching
      ) {
        return "REVIEW";
      }

      return "AGENT_RECOMMENDED";
    },
    getStepPathname: () => "marketing/pitch",
    hierarchy: 19,
  },
  {
    identifier: "AGENT_RECOMMENDED",
    getPreviousStep: () => "PITCH",
    getNextStep: (listing) => {
      if (typeof listing.wantsAgentMatching === "boolean") {
        return "REVIEW";
      }

      return listing.sellerAgentId || listing.usesRecommendedAgent
        ? "REVIEW"
        : "AGENT_SEARCH";
    },
    getStepPathname: () => "agent/recommended",
    hierarchy: 20,
    openConditions: (listing) =>
      !listing.sellerAgentId &&
      !listing.usesRecommendedAgent &&
      !listing.wantsAgentMatching,
  },
  {
    identifier: "AGENT_SEARCH",
    getPreviousStep: () => "AGENT_RECOMMENDED",
    getNextStep: () => "REVIEW",
    getStepPathname: () => "agent/search",
    hierarchy: 21,
    openConditions: (listing) =>
      !listing.sellerAgentId &&
      !listing.wantsAgentMatching &&
      !listing.usesRecommendedAgent,
  },
  {
    identifier: "REVIEW",
    getPreviousStep: (listing) => {
      if (
        listing.sellerAgentId ||
        listing.usesRecommendedAgent ||
        listing.wantsAgentMatching
      ) {
        return "PITCH";
      }

      return "AGENT_RECOMMENDED";
    },
    getNextStep: (listing) =>
      listing.status === "DRAFT" || listing.status === "REVIEWING"
        ? "CHECKS"
        : "FINISH",
    getStepPathname: () => "review",
    hierarchy: 22,
  },
  {
    identifier: "CHECKS",
    getPreviousStep: () => "REVIEW",
    getNextStep: () => "FINISH",
    getStepPathname: () => "review/checks",
    hierarchy: 23,
  },
  {
    identifier: "FINISH",
    getPreviousStep: () => "REVIEW",
    getNextStep: () => null,
    getStepPathname: () => "review/finish",
    hierarchy: 24,
    openConditions: (listing) => listing.status !== "DRAFT",
  },
];

export const listingEditorStepsMap = listingEditorSteps.reduce(
  (acc, step) => {
    acc[step.identifier] = step;
    return acc;
  },
  {} as Record<ListingStepType, ListingEditorStepData>,
);

export function listingStepCompare(
  a: ListingStepType,
  b: ListingStepType,
): number {
  return (
    listingEditorStepsMap[a].hierarchy - listingEditorStepsMap[b].hierarchy
  );
}

export function pathnameToStep(pathname: string): ListingStepType | null {
  let finalPathname = pathname;

  const queryIndex = finalPathname.indexOf("?");
  if (queryIndex !== -1) {
    finalPathname = finalPathname.slice(0, queryIndex);
  }

  if (finalPathname.endsWith("/")) {
    finalPathname = finalPathname.slice(0, -1);
  }

  const step = listingEditorSteps.find((step) => {
    return finalPathname.endsWith(step.getStepPathname());
  });

  return step ? step.identifier : null;
}

export function getRedirectPathnameIfNeeded(
  listing: Listing,
  pathname: string,
): string | null {
  if (listing.status !== "DRAFT") {
    const stepIdentifier = pathnameToStep(pathname);
    const step = stepIdentifier ? listingEditorStepsMap[stepIdentifier] : null;

    if (step?.openConditions && !step.openConditions(listing)) {
      const previous = step.getPreviousStep(listing);
      return previous
        ? listingEditorStepsMap[previous].getStepPathname()
        : null;
    }

    return null;
  }

  if (listing.step === "REVIEW") {
    return null;
  }

  const lastCompletedStepIdentifier = listing.lastCompletedStep;
  const currentStepIdentifier = pathnameToStep(pathname);

  if (!lastCompletedStepIdentifier) {
    if (currentStepIdentifier === "CONFIRM_LOCATION") {
      return null;
    }

    return listingEditorSteps[0].getStepPathname();
  }

  if (!currentStepIdentifier) {
    return null;
  }

  const lastCompletedStep = listingEditorStepsMap[lastCompletedStepIdentifier];
  const nextStepIdentifier = lastCompletedStep.getNextStep(listing);

  if (!nextStepIdentifier) {
    return null;
  }

  const nextStep = listingEditorStepsMap[nextStepIdentifier];
  const currentStep = listingEditorStepsMap[currentStepIdentifier];

  if (nextStep.hierarchy <= currentStep.hierarchy) {
    return nextStep.getStepPathname();
  }

  if (currentStep.openConditions && !currentStep.openConditions(listing)) {
    const previous = currentStep.getPreviousStep(listing);
    return previous ? listingEditorStepsMap[previous].getStepPathname() : null;
  }

  return null;
}

export function getPreviousStep(
  listing: PartialListingWithStep,
): ListingStepType | null {
  const step = listingEditorStepsMap[listing.step];
  return step.getPreviousStep(listing);
}

export function getNextStep(
  listing: PartialListingWithStep,
  metadata?: any,
): ListingStepType | null {
  const step = listingEditorStepsMap[listing.step];
  return step.getNextStep(listing, metadata);
}

export function getStepPathname(step: ListingStepType): string {
  return listingEditorStepsMap[step].getStepPathname();
}

export type ListingStepSection =
  | "yourDetails"
  | "businessDetails"
  | "media"
  | "marketing"
  | "agent"
  | "review";

export function getStepSection(step: ListingStepType): ListingStepSection {
  const pathname = getStepPathname(step);
  if (pathname.startsWith("business/")) return "businessDetails";
  if (pathname.startsWith("media/")) return "media";
  if (pathname.startsWith("marketing/")) return "marketing";
  if (pathname.startsWith("agent/")) return "agent";
  if (pathname.startsWith("review")) return "review";
  return "yourDetails";
}

export const EDITOR_URL = "/manage-listing/:id/:step_pathname";

export function buildStepUrlFromPathname(listingId: number, pathname: string) {
  return EDITOR_URL.replace(":id", listingId.toString()).replace(
    ":step_pathname",
    pathname,
  );
}

export function buildStepUrl(listing: Listing, step: ListingStepType): string {
  const pathname = getStepPathname(step);
  return buildStepUrlFromPathname(listing.id, pathname);
}

export function getNewFinalStep(
  listing: PartialListingWithStep,
): ListingStepType | undefined {
  const lastCompletedStepIdentifier = listing.lastCompletedStep;
  const newStepIdentifier = listing.step;

  if (!lastCompletedStepIdentifier) {
    return newStepIdentifier;
  }

  const newStepData = listingEditorStepsMap[newStepIdentifier];
  const lastCompletedStep = listingEditorStepsMap[lastCompletedStepIdentifier];

  if (newStepData.hierarchy >= lastCompletedStep.hierarchy) {
    return newStepIdentifier;
  }

  return undefined;
}

export function getTotalStepCount(): number {
  return listingEditorSteps.length;
}

export function getApplicableStepCount(listing: Partial<Listing>): number {
  return listingEditorSteps.filter(
    (step) => !step.openConditions || step.openConditions(listing),
  ).length;
}

export function getApplicableStepIndex(
  listing: Partial<Listing>,
  pathname: string,
): number {
  const applicableSteps = listingEditorSteps.filter(
    (step) => !step.openConditions || step.openConditions(listing),
  );
  const currentStep = pathnameToStep(pathname);
  if (!currentStep) return 0;
  const idx = applicableSteps.findIndex(
    (step) => step.identifier === currentStep,
  );
  return idx === -1 ? 0 : idx;
}

export function getStepIndex(step: ListingStepType): number {
  return listingEditorSteps.findIndex((s) => s.identifier === step);
}

export function getStepIndexFromPathname(pathname: string): number {
  const step = pathnameToStep(pathname);
  return step ? getStepIndex(step) : 0;
}
